/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import DiscussionPage, { type IDiscussionPageAttrs } from 'flarum/forum/components/DiscussionPage';
import DiscussionListPane from 'flarum/forum/components/DiscussionListPane';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Page from 'flarum/common/components/Page';
import ReviewControls from '../utils/ReviewControls';
import ArticleHero from 'flarum/orell-articles/components/ArticleHero';
import LiteraryWorkPageOrderButton from 'flarum/orell-articles/components/LiteraryWorkPageOrderButton';
import ItemList from 'flarum/common/utils/ItemList';
import * as terms from '@fof-terms';
import CreateReviewPrimaryButton from './CreateReviewPrimaryButton';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import Dropdown from 'flarum/common/components/Dropdown';
const UpdateAlert = terms.components.UpdateAlert;
import type Discussion from 'flarum/common/models/Discussion';
import type Mithril from 'mithril';
import type { ApiResponseSingle } from 'flarum/common/Store';

/**
 * DiscussionPage for a single review. Routed in ../extend/setupRouting.
 *
 * CORE_CODE_OVERRIDE
 */
export default class ReviewPage extends DiscussionPage {
	/**
	 * Copied from core, driven by app.reviews instead of app.discussions.
	 *
	 * CORE_CODE_OVERRIDE
	 */
	oninit(vnode: Mithril.Vnode<IDiscussionPageAttrs, this>) {
		// Grandparent oninit(), skipping DiscussionPage's.
		Reflect.apply(Page.prototype.oninit, this, [vnode]);

		this.load();

		// Enable the pane (hidden by default) so coming from another review page does not
		// redraw it — slow, and it breaks the pane's event handlers.
		if (app.reviews.hasItems() || app.reviews.isLoading()) {
			app.pane!.enable();
			app.pane!.hide();
		}

		this.bodyClass = 'App--review';
	}

	/**
	 * CORE_CODE_OVERRIDE
	 */
	view() {
		const discussion = this.discussion;

		const review = discussion && discussion.review();
		const article = review && review.article();
		let canWriteReview: boolean | undefined;

		if (article) {
			// One review per article, unless the existing one is rating-only.
			const ownReview = article.ownReview();

			canWriteReview = !ownReview || ownReview.isSimple();
		}

		return (
			<div className="DiscussionPage ReviewPage">
				<UpdateAlert />
				<DiscussionListPane state={app.reviews} />
				<div className="DiscussionPage-discussion">
					{discussion ? (
						[
							<ArticleHero helperElement={true} isReview={true} articleDetailsItems={new ItemList()} article={article} />,
							<ArticleHero isReview={true} articleDetailsItems={new ItemList()} article={article} />,
							<div className="container">
								<div className="sideNavContainer">
									<div className="sideNav"></div>
									<div className="sideNavOffset">
										{this.mainContent().toArray()}

										<div className="ReviewPage-extra">
											{canWriteReview && (
												<div className={`ReviewPage-addReview ${discussion.commentCount()! > 1 ? 'hasComments' : ''}`}>
													<CreateReviewPrimaryButton
														article={article}
														label={app.translator.trans('glowingblue-orell-rezensionen.forum.review_page.review_same_article_label')}
													/>
												</div>
											)}

											<hr />

											<LiteraryWorkPageOrderButton article={article} />
										</div>
									</div>
								</div>
							</div>,
						]
					) : (
						<LoadingIndicator />
					)}
				</div>
			</div>
		);
	}

	/**
	 * Serves ReviewControls next to the DiscussionControls, in a dropdown of their own.
	 */
	sidebarItems() {
		// The page always has a discussion by the time the sidebar is built.
		const discussion = this.discussion!;

		const items = super.sidebarItems();

		const discussionControls = DiscussionControls.controls(discussion, this);
		const reviewControls = ReviewControls.controls(discussion, this);
		discussionControls.merge(reviewControls);

		discussionControls.remove('reply');
		discussionControls.remove('userSeparator');

		const controls = discussionControls.toArray();

		if (!discussionControls.isEmpty()) {
			items.add(
				'of_controls',
				<Dropdown
					menuClassName="Dropdown-menu--right"
					buttonClassName="Button Button--secondary"
					label={app.translator.trans('glowingblue-orell-rezensionen.forum.discussion.nav.settings')}
				>
					{controls}
				</Dropdown>,
				100
			);
		}

		return items;
	}

	show(discussion: ApiResponseSingle<Discussion>) {
		super.show(discussion);

		const review = discussion.review();

		if (review && review.cannotExportUnapproved()) {
			app.alerts.show(app.translator.trans('glowingblue-orell-rezensionen.forum.review_page.cannot_export_unapproved_alert'));
		}
	}
}
